/** 处理自定义文件夹结构生成以及对应文件产出的功能 */
import { IBigPlank, PartType } from '@/partTypes'
import {
  getCurlyBracketText,
  incrementStartNum,
  removeBase64Head,
} from '@/util/common'
import { joinPath, splitPath } from '@/util/fileSystem'
import JsZip from '@/util/jszip'
import { getPaibanTime } from '@/util/tag'
import { Folder } from '@/views/fileManage/components/m-custom-folder/types'
import { isFileTypeClassifiable } from '@/views/fileManage/components/m-custom-folder/utils/folderAuthority'
import { dataToQt } from '@/views/newPaiban/util/downLoadNC'
import axios from 'axios'
import FileSaver from 'file-saver'
import pLimit from 'p-limit'

import { sanitizeFileName } from '../util'
import {
  createNotClassFolder,
  enrichFileWithDefaultPlankData,
  ensureFile,
  getSupportedFile,
  getZipEntryPath,
  isFolderNameExistStartNum,
  isNeedGenStartNum,
  isSupportedFile,
  replaceTemplate,
} from './common'
import {
  FileNode,
  FileSource,
  FolderNode,
  IEnricherParams,
  IExtraData,
  PathItem,
  TreeNode,
} from './types'

/**
 * @description 根据用户自定义文件夹结构和收集到的数据，构建一个携带数据的文件树
 */
export class FolderStructureBuilder {
  private rootNode!: FolderNode
  /** 已经被使用过的文件 */
  private usedFiles = new Set<FileSource>()
  public collector: ContextCollector | null = null
  public folderConfig: Folder | null = null
  /** 文件路径分隔符 */
  static sep = '/'
  constructor(collector?: ContextCollector, folderConfig?: Folder) {
    if (collector) {
      this.collector = collector
    }
    if (folderConfig) {
      this.folderConfig = folderConfig
    }
  }
  /** @description 设置文件夹配置 */
  setFolderConfig(folderConfig: Folder) {
    this.folderConfig = folderConfig
  }
  /** @description 设置收集器 */
  setCollector(collector: ContextCollector) {
    this.collector = collector
  }
  /** @description 运行构建器 */
  public run() {
    if (!this.collector || !this.folderConfig) {
      throw new Error('参数异常')
    }
    this.initStructure()
  }
  /** @description  根据用户模板，进行结构初始化，完全基于 folder 类型的结构同时会将对应文件信息放入到指定文件夹下 */
  initStructure() {
    this.collector?.executeEnrichers()
    const fileSources = this.collector?.getFiles() ?? []
    // 初始化根节点
    this.rootNode = {
      id: this.folderConfig?.id ?? '',
      name: '', // 初始化时为空，后续会重命名
      type: 'folder',
      path: '',
      createTime: new Date().getTime(),
      children: [],
      availableFiles: fileSources,
      config: this.folderConfig ?? ({} as Folder),
    }
    this.buildFolderStructure(this.rootNode, this.folderConfig?.children ?? [])
    this.allocateFilesToFolders(this.rootNode, fileSources)
    this.handleUnusedFiles(fileSources)
    this.assignNames(this.rootNode)
    this.loopTree(this.rootNode)
    this.generateFullPath()
  }
  /** @description 对整个树做一轮循环，执行文件/文件夹携带特殊函数以及一起其他的，你觉得需要在最后处理的(或者什么莫名其妙的函数(😊)),在此函数后将进行路径组合 */
  private loopTree(node: FolderNode) {
    const [fileChildren, folderChildren] = node.children.reduce(
      (acc, child) => {
        if (child.type === 'file') {
          acc[0].push(child)
        } else {
          acc[1].push(child)
        }
        return acc
      },
      [[], []] as [FileNode[], FolderNode[]]
    )
    // 处理文件节点
    fileChildren.forEach((fileChild) => {
      // 文件节点如存在特殊操作则执行特殊操作
      if (fileChild.specialHandler) {
        fileChild.specialHandler(fileChild, node)
      }
    })
    folderChildren.forEach((folderChild) => {
      this.loopTree(folderChild)
    })
    // 循环期间对文件夹名的递增序号进行替换
    this.addStartNum(node)
  }
  /** @description 处理文件没有被使用的情况，需要将所有未使用的文件，单独存放到未分类文件夹中 */
  private handleUnusedFiles(fileSources: FileSource[]) {
    const unusedFiles = fileSources
      .filter((file) => !this.usedFiles.has(file))
      .map((file) => {
        // 未使用的文件如果存在folderGroupId，后续的文件分配会导致再次被拒绝添加进入【未使用文件】夹,未使用文件夹也不需要此 id 来定位了
        Reflect.deleteProperty(file, 'folderGroupId')
        return file
      })
    if (!unusedFiles.length) return
    const folderConfig = createNotClassFolder()
    const notClassFolder = this.createSingleFolderNode(
      folderConfig,
      this.rootNode,
      unusedFiles
    )
    this.rootNode.children.push(notClassFolder)
    this.allocateFilesToFolders(notClassFolder, unusedFiles)
  }
  /** @description 构建文件夹结构 */
  private buildFolderStructure(
    parentNode: FolderNode,
    folderConfigs: Folder[]
  ) {
    /** 根据分类属性创建多个文件夹 */
    const createClassifiedFolderNodes = (
      config: Folder,
      files: FileSource[]
    ): FolderNode[] => {
      const { classifyChains } = config.accept
      const classifiableFiles = files.filter((file) =>
        isFileTypeClassifiable(file.type, classifyChains)
      )
      const classificationMap = new Map<string, FileSource[]>()
      classifiableFiles.forEach((file) => {
        const { customRecord } = file
        const nCustomRecord = Object.fromEntries(
          Object.entries(window.$copy(customRecord ?? {})).map(
            ([key, value]) => [key, value ? String(value) : '']
          )
        )
        const classificationKey = classifyChains.reduce(
          (key, field) =>
            `${key}${replaceTemplate(field, nCustomRecord) ?? ''}`,
          ''
        )
        const existingFiles = classificationMap.get(classificationKey) ?? []
        classificationMap.set(classificationKey, [...existingFiles, file])
      })
      // 对生成的分类做筛选：组内至少有一种文件类型在该文件夹模板全链路上可被接纳，才生成分类文件夹
      const filteredGroups = Array.from(classificationMap.values()).filter(
        (classifiedFiles) =>
          classifiedFiles.some((file) =>
            isSupportedFile(config, file.type, file.folderGroupId ?? '')
          )
      )
      return filteredGroups.map((classifiedFiles) =>
        this.createSingleFolderNode(config, parentNode, classifiedFiles)
      )
    }
    // 根据模板创建文件树
    folderConfigs.forEach((config) => {
      if (config.accept?.classifyChains?.length) {
        const nodes = createClassifiedFolderNodes(
          config,
          parentNode.availableFiles
        )
        parentNode.children.push(...nodes)
      } else {
        const node = this.createSingleFolderNode(
          config,
          parentNode,
          parentNode.availableFiles
        )
        parentNode.children.push(node)
      }
    })
    // 递归处理子文件夹
    const folderChildren = parentNode.children.filter(
      (child): child is FolderNode => child.type === 'folder'
    )
    folderChildren.forEach((folderChild) => {
      if (folderChild.config.children.length > 0) {
        this.buildFolderStructure(folderChild, folderChild.config.children)
      }
    })
  }
  /**
   *  @description 为所有文件夹分配文件
   * @param node 文件夹节点
   * @param fileSources 文件源列表
   * @param isRecursion
   */
  private allocateFilesToFolders(node: FolderNode, fileSources: FileSource[]) {
    const { supportedFiles } = node.config.accept
    fileSources.forEach((fileSource) => {
      const { folderGroupId, type, name, ext, specialHandler } =
        fileSource ?? {}
      // 1.检查当前文件夹是否支持此类型的文件
      const supportedFile = getSupportedFile(supportedFiles, type)
      if (!supportedFile) return
      /**
       * 2. 后端文件类型中会存在一个folder_group_id(把文件树的 ID 返回了),存在值是说明后端进行了分类，所以对应的文件放置时还需要判断和文件夹节点的 ID 是否相等
       * 不做此处理的话，那么分类后的文件夹就会接受到错误的文件
       */
      if (folderGroupId && folderGroupId !== node.id) return
      const fileNode: FileNode = {
        id: node.id,
        name,
        type: 'file',
        path: '',
        source: fileSource,
        createTime: node.createTime,
        ext: ext ?? supportedFile.ext ?? '',
        specialHandler,
      }
      // 当文件被使用后，就记录(未被使用的文件将单独存放)
      this.usedFiles.add(fileSource)
      node.children.push(fileNode)
    })

    // 递归处理子文件夹
    const folderChildren = node.children.filter(
      (child): child is FolderNode => child.type === 'folder'
    )
    folderChildren.forEach((folderChild) => {
      this.allocateFilesToFolders(folderChild, folderChild.availableFiles)
    })
  }
  /** @description 为所有节点分配正确的名称和路径 */
  private assignNames(node: FolderNode) {
    // 为文件夹分配名称
    node.name = sanitizeFileName(
      this.generateFolderName(node.config, node.availableFiles)
    )
    // 路径和名称相同，后续可以按需分配完整路径和组合路径
    node.path = node.name
    // 递归处理子文件夹
    const folderChildren = node.children.filter(
      (child): child is FolderNode => child.type === 'folder'
    )
    folderChildren.forEach((folderChild) => {
      this.assignNames(folderChild)
    })
  }
  /** @description 为文件夹名称添加递增序号值(实际是当前节点的子节点携带了自增的序号值) */
  private addStartNum(node: FolderNode) {
    // 实际上是本文件夹添加需要，但从父文件向下找处理起来比较简单
    if (!isFolderNameExistStartNum(node)) return
    const needStartNumFolders = new Map<string, FolderNode[]>()
    const folderChildren = node.children.filter(
      (child): child is FolderNode => child.type === 'folder'
    )
    folderChildren.forEach((child) => {
      if (isNeedGenStartNum(child.config)) {
        const target = needStartNumFolders.get(child.id) ?? []
        target.push(child)
        needStartNumFolders.set(child.id, target)
      }
    })
    needStartNumFolders.forEach((v) => {
      // 为文件名添加递增序号(在文件名生成之后，只有一个文件的情况下不添加)
      let startNum: string | number = v[0]?.config.startNum ?? 1
      v.forEach((child) => {
        let orderValue = ''
        // 单个文件以及不存在子文件夹的情况下不添加递增序号
        if (v.length > 1 && child.children.length != 0) {
          orderValue = String(startNum)
        }
        const name = replaceTemplate(child.name, {
          // 只有一个文件的情况下不添加递增序号,仅将{order}做替换
          order: orderValue,
        })
        if (orderValue) {
          startNum = incrementStartNum(startNum)
        }
        child.name = name
        child.path = name
      })
    })
  }
  /** @description 生成文件夹名称 */
  private generateFolderName(
    folderConfig: Folder,
    fileSources: FileSource[]
  ): string {
    const { nameFields } = folderConfig
    let nameComponents = nameFields.map((field) => {
      switch (field.type) {
        case 'text':
          return field.value
        case 'template': {
          // 提取模板中的字段名
          const templateKeys = getCurlyBracketText(field.value).map((key) =>
            key.replace(/[{}]/g, '')
          )
          // 从文件源中提取数据并合并
          const templateData = templateKeys.reduce((data, key) => {
            const uniqueValues = new Set<string>()
            fileSources.forEach((fileSource) => {
              const value = fileSource.customRecord?.[key]
              if (value || value === 0) {
                const vals = Array.isArray(value) ? value : [value]
                vals.forEach((v) => {
                  uniqueValues.add(String(v))
                })
              }
            })
            data[key] = Array.from(uniqueValues).join('_')
            return data
          }, {} as Record<string, string>)
          return replaceTemplate(field.value, templateData)
        }
        default:
          return ''
      }
    })
    if (!nameFields.length) nameComponents = [folderConfig.name]
    return nameComponents.join('')
  }
  /** @description 获取构建好的路径结构 */
  public async getPathStructure() {
    return this.buildPathStructure(this.rootNode)
  }
  /** @description 获取构建好的树结构 */
  public getNodeStructure(): TreeNode {
    return this.rootNode
  }
  /** 获取根文件名称 */
  public getRootFolderName(): string {
    return this.getNodeStructure().name
  }
  /** 获取根文件夹路径 */
  public getRootFolderPath(): string {
    return this.getNodeStructure().path
  }
  /** @description 将树结构转换为路径列表，便于导出工具使用 */
  private buildPathStructure(node: TreeNode): PathItem[] {
    if (!node) throw new Error('节点不存在')
    const result: PathItem[] = []
    if (node.type === 'folder') {
      const folderNode = node as FolderNode
      // 添加文件夹路径（根节点除外）
      if (folderNode.name) {
        result.push({
          path: folderNode.path,
          type: 'folder',
          metadata: {
            id: folderNode.id,
            createTime: folderNode.createTime,
          },
        })
      }
      // 递归处理子节点
      folderNode.children.forEach((child) => {
        const childResults = this.buildPathStructure(child)
        result.push(...childResults)
      })
    } else {
      // 文件节点
      const fileNode = node as FileNode
      const { source } = fileNode
      result.push({
        path: fileNode.path,
        type: 'file',
        error: fileNode.source.error,
        metadata: {
          id: fileNode.id,
          fileName: fileNode.name,
          source: source,
          createTime: fileNode.createTime,
          ext: fileNode.ext,
        },
      })
    }
    return result
  }
  /** @description 创建单个文件夹节点 */
  createSingleFolderNode(
    config: Folder,
    parentNode: FolderNode,
    availableFiles: FileSource[]
  ): FolderNode {
    return {
      id: config.id,
      name: '', // 初始化为空，后续重命名
      type: 'folder',
      path: '',
      createTime: parentNode.createTime,
      children: [],
      availableFiles,
      config,
    }
  }
  /**
   * @description 为当前文件树所有节点生成其完整的 path
   * @param node 当前节点 (不传值则为根节点)
   * @param parentPath 父节点路径
   */
  public generateFullPath(node?: TreeNode, parentPath = '') {
    // 首次使用根节点
    node = node ?? this.rootNode
    if (node.type === 'folder') {
      const folderNode = node as FolderNode
      folderNode.path = joinPath(
        FolderStructureBuilder.sep,
        parentPath,
        this.getLimitFolderName(folderNode)
      )
      folderNode.children.forEach((child) => {
        this.generateFullPath(child, folderNode.path)
      })
    } else {
      const fileNode = node as FileNode
      fileNode.path = joinPath(
        FolderStructureBuilder.sep,
        parentPath,
        fileNode.name + fileNode.ext
      )
    }
  }
  /**
   * @description 获取指定节点的完整路径（从根节点遍历查找，每次重新构建）
   * @param targetNode 目标节点
   * @returns 完整路径，如果节点不存在则返回空字符串
   */
  public getNodePath(targetNode: TreeNode): string {
    const pathSegments: string[] = []
    // 递归查找并构建路径的内部函数
    const findAndBuildPath = (
      currentNode: TreeNode,
      targetNode: TreeNode,
      pathSegments: string[]
    ) => {
      // 找到目标节点
      if (currentNode === targetNode || currentNode.id === targetNode.id) {
        // 添加当前节点名到路径
        if (currentNode.type === 'folder') {
          const folderNode = currentNode as FolderNode
          if (folderNode.name) {
            pathSegments.push(folderNode.name)
          }
        } else {
          const fileNode = currentNode as FileNode
          pathSegments.push(fileNode.name + fileNode.ext)
        }
        return true
      }
      // 只有文件夹节点才有子节点
      if (currentNode.type === 'folder') {
        const folderNode = currentNode as FolderNode
        // 添加当前文件夹名到路径
        if (folderNode.name) {
          pathSegments.push(folderNode.name)
        }
        // 递归查找子节点
        for (const child of folderNode.children) {
          if (findAndBuildPath(child, targetNode, pathSegments)) {
            return true
          }
        }
        // 未找到，回溯移除当前路径段
        if (folderNode.name) {
          pathSegments.pop()
        }
      }
      return false
    }
    // 从根节点开始查找
    const found = findAndBuildPath(this.rootNode, targetNode, pathSegments)
    return found ? pathSegments.join(FolderStructureBuilder.sep) : ''
  }
  /** @description 限制文件夹名称长度 */
  private getLimitFolderName(folder: FolderNode) {
    const {
      config: { nameLimitSize },
    } = folder
    return folder.name.slice(0, nameLimitSize || 60)
  }
  clear() {
    this.usedFiles.clear()
    this.rootNode = undefined as any
    this.collector?.clear()
    this.collector = null
    this.folderConfig = null
  }
}

/**
 * @description 收集自定义文件夹在项目内散落的文件信息/对应数据，以及当次下载相关联的数据，并将数据和文件进行部分数据关联
 */
class ContextCollector {
  /** @description 初始化时的时间 */
  static InitTime: string | null = null
  /** @description  原始Nc数据 */
  private originNcData: IBigPlank[] = []
  /** @description 原始板件数据 */
  private parts: PartType[] = []
  /** @description nc设置 */
  private ncSetting!: INcSetting
  /** @description 额外的一些可以被关联的数据(可以被模板字符串使用的字段) */
  private extraData: IExtraData = {
    productionLineName: '',
    downloadEnv: 'standard',
  }
  /** @description 文件如果想处理一下的话请添加处理函数 */
  private enrichers: {
    defaults: ((args: IEnricherParams) => void)[]
    extras: ((args: IEnricherParams) => void)[]
  } = {
    defaults: [],
    extras: [],
  }
  /** @description 在执行enrichers之前的初始化函数，每次executeEnrichers时只执行一次 */
  private initializers: {
    defaults: (() => void)[]
    extras: (() => void)[]
  } = {
    defaults: [],
    extras: [],
  }
  public fileSources: FileSource[] = []
  constructor(
    enrichers: ((args: IEnricherParams) => void)[] = [],
    initializers: (() => void)[] = []
  ) {
    this.enrichers.defaults.push(...enrichers)
    this.initializers.defaults.push(...initializers)
  }
  /** @description 添加自定义文件处理器，在执行 clear 后，此代码会被删除 */
  public registerEnricher(enricher: (args: IEnricherParams) => void) {
    this.enrichers.extras.push(enricher)
  }
  /** @description 注册初始化函数，在每次executeEnrichers时执行一次，在执行 clear 后，此代码会被删除 */
  public registerInitializer(initializer: () => void) {
    this.initializers.extras.push(initializer)
  }
  /** @description 为收集器设置 nc 设置 */
  public setNcSetting(ncSetting: INcSetting) {
    this.ncSetting = ncSetting
  }
  /** @description 获取 nc 设置 */
  public getNcSetting(): INcSetting {
    return this.ncSetting
  }
  /**
   * @description 为收集器设置额外的一些可以被关联的数据(可以被模板字符串使用的字段)
   */
  public addExtraData(data: IExtraData) {
    Object.assign(this.extraData, data)
  }
  /**
   * @description 收集项目内散落在各处需要当次下载的 nc 相关文件，内部将按照类型自动分配数据和文件
   *
   */
  public addFiles(
    files: Omit<FileSource, 'customRecord'>[] | Omit<FileSource, 'customRecord'>
  ) {
    const fileArray = Array.isArray(files) ? files : [files]
    this.fileSources.push(...(fileArray as FileSource[]))
  }
  /** 执行所有自定义记录处理器 */
  private enrichFileWithCustomData(file: FileSource) {
    const fns = [...this.enrichers.defaults, ...this.enrichers.extras]
    fns.forEach((enricher) =>
      enricher({
        originNcData: this.originNcData,
        parts: this.parts,
        ncSetting: this.ncSetting,
        extraData: this.extraData,
        file,
      })
    )
  }

  /** @description  为收集器设置原始NC数据，会根据传入的数据再 nc 回来时进行数据对比 */
  public setOriginNcData(data: IBigPlank[] | string) {
    this.originNcData = typeof data === 'string' ? JSON.parse(data) : data
    this.setOriginParts(this.originNcData.flatMap((item) => item.parts))
  }
  /** @description 为收集器添加原始板件信息，会根据传入的数据再 nc 回来时进行数据对比，和 setOriginNcData 方法二选一进行传入*/
  public setOriginParts(parts: PartType[]) {
    this.parts = parts
  }
  /** @description  获取原始发送给NC接口的板件数据 */
  public getOriginNcData(): IBigPlank[] {
    return this.originNcData
  }
  /** @description  下载需要下载的文件 */
  public async downloadFiles(concurrencyLimit = 10) {
    const filesToDownload = this.fileSources.filter(
      (f) => f.raw.kind === 'url' && /^https?:\/\//.test(f.raw.value)
    )
    const limit = pLimit(concurrencyLimit)
    if (!filesToDownload.length) return
    const downloadTasks = filesToDownload.map((file) =>
      limit(async () => {
        try {
          if (file.raw.kind !== 'url') return file
          const url = file.raw.value
          const res = await axios.get(url, { responseType: 'arraybuffer' })
          const buf = res.data as ArrayBuffer
          file.file = new Blob([buf])
          return file
        } catch (err: unknown) {
          let detail = '未知错误'
          if (axios.isAxiosError(err)) {
            detail = err.response?.statusText || err.message
          } else if (err instanceof Error) {
            detail = err.message
          }
          throw new Error(`文件：${file.name} 下载失败，错误信息：${detail}`)
        }
      })
    )
    const results = await Promise.allSettled(downloadTasks)
    // 处理结果，添加错误信息到文件
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        const file = filesToDownload[index]
        console.error(result.reason?.message)
        // 可以选择保留原 URL 或设置错误标记
        file.error = {
          msg: result.reason?.message ?? '下载失败',
        }
      }
    })
    const errorMsg = results
      .filter(
        (result): result is PromiseRejectedResult =>
          result.status === 'rejected'
      )
      .map((result) => result.reason?.message)
    return {
      success: errorMsg.length === 0,
      data: errorMsg,
    }
  }
  /** @description 执行初始化函数 */
  private executeInitializers() {
    const fns = [...this.initializers.defaults, ...this.initializers.extras]
    fns.forEach((initializer) => initializer())
  }
  /** @description 执行自定义处理器,为文件注入数据 */
  public executeEnrichers() {
    // 先执行初始化函数
    this.executeInitializers()
    // 再为每个文件注入数据
    this.fileSources.forEach((file) =>
      this.enrichFileWithCustomData(file as FileSource)
    )
  }
  /** @description  获取收集到的文件源,（调用前请先执行 executeEnrichers 方法） */
  public getFiles(): FileSource[] {
    return this.fileSources
  }
  /** @description  清空收集的数据 */
  public clear(isClearDefault = false) {
    this.fileSources = []
    this.originNcData = []
    this.parts = []
    this.enrichers.extras = []
    this.initializers.extras = []
    this.extraData = {
      productionLineName: '',
      downloadEnv: 'standard',
    }
    this.ncSetting = {} as INcSetting
    if (isClearDefault) {
      this.enrichers.defaults = []
      this.initializers.defaults = []
    }
  }
}

/**
 * @description nc 构建器使用的导出器，便于导出各种类型的文件
 */
export class Exporter {
  constructor(public builder: FolderStructureBuilder) {}
  /**
   * @description  导出ZIP文件（使用 jszip 库）
   * @param isAutoSave  是否自动保存
   * @param isFullZip  是否导出一个完整的 zip/ 因为某些限制，可能需要文件不要第一层
   */
  async exportZip(isAutoSave = true, isFullZip = true) {
    try {
      // 导出 zip 需要下载文件
      await this.builder?.collector?.downloadFiles()
      const pathStructures = await this.builder.getPathStructure()
      const { success: isSuccess, errorMsg } =
        this.checkFilesError(pathStructures)
      if (!isSuccess) {
        return {
          success: false,
          errorMsg,
        }
      }
      const name = this.builder.getRootFolderName() ?? ''
      const zip = new JsZip() as import('jszip')
      console.log(this.builder.getNodeStructure())
      pathStructures.forEach((pathStructure) => {
        if (pathStructure.type === 'file') {
          const {
            metadata: { source },
          } = pathStructure
          // 数据转换
          if (source) ensureFile(source)
          const result: Blob = source?.file as Blob
          if (!result) return
          const path = getZipEntryPath(
            pathStructure.path,
            this.builder.getRootFolderPath(),
            isFullZip
          )
          zip.file(path, result)
        }
      })
      if (isAutoSave) {
        FileSaver.saveAs(await zip.generateAsync({ type: 'blob' }), name)
        return
      }
      return { success: true, zip }
    } catch (error) {
      return { success: false, error }
    }
  }
  /** @description 检查文件是否存在错误 */
  private checkFilesError(pathStructures: PathItem[]) {
    const files = pathStructures.filter(
      (pathStructure) => pathStructure.type === 'file'
    )
    const errors = files.filter((file) => file.error?.msg)
    return {
      success: errors.length === 0,
      errorMsg: errors.map((v) => v.error?.msg),
    }
  }
  /**
   * @description 导出数据到 qt 客户端
   */
  async exportToQtClient() {
    // qt 的数据不用下载，qt 那边会自己下载
    const pathStructures = await this.builder.getPathStructure()
    const { success: isSuccess, errorMsg } =
      this.checkFilesError(pathStructures)
    if (!isSuccess) {
      return {
        success: false,
        errorMsg,
      }
    }
    const qtData: {
      url: string
      fileName: string
      originFile: string
      fileType: string
    }[] = []
    // 下载 json/采购单/生产批次文件柜柜无法下载，不传输
    const ignoreFileTypes = [
      'layout_json',
      'product_export_batch_excel',
      'worker_shop_excel',
    ]
    for (const pathStructure of pathStructures) {
      if (pathStructure.type === 'file') {
        const {
          path,
          metadata: { source, fileName, ext },
        } = pathStructure
        const { type: fileType, raw } = source ?? {}
        const { kind, value } = raw ?? {}
        let result: string = value as string
        if (kind === 'base64') {
          result = removeBase64Head(result)
        } else if (ignoreFileTypes.includes(fileType ?? '')) {
          continue
        }
        // 下载给 QT 的数据一定需要是一个字符串(链接或者 base64 数据)，否则下载会失败
        if (typeof result !== 'string') {
          continue
        }
        const { folderPath } = splitPath(path)
        const name = `${fileName}${ext}`
        qtData.push({
          url: result,
          fileName: name,
          originFile: folderPath,
          /** fileType就俩参数 base64/url  base64 只支持图片 */
          fileType: kind === 'base64' ? 'base64' : 'url',
        })
      }
    }
    dataToQt(qtData, this.builder.collector?.getOriginNcData() ?? [])
    return { success: true }
  }
  /** @description  清空导出器 */
  clear() {
    this.builder.clear()
  }
}

const collector = new ContextCollector(
  [enrichFileWithDefaultPlankData],
  [() => (ContextCollector.InitTime = getPaibanTime(true))]
)

export { collector, ContextCollector }
